'use client';

import { type ReactNode, useState } from 'react';

import Link from '~/(tenant)/_components/tenant-link';

import { PackageSessionOptionSelect } from '../package-session-option-select';

export interface LandingPackageOptionView {
  id: string;
  bookingCount: number;
  /** Server-rendered title of this option. */
  title: ReactNode;
  /** Server-rendered price, sessions line, description and features. */
  content: ReactNode;
}

/**
 * Interactive part of a website packages card whose package is sold at
 * several session counts: the picker, the chosen option's price, and a CTA
 * that opens /packages focused on that exact option. Each option's content
 * is server-rendered; this only swaps which one is shown.
 */
export function PackageSessionOptionsCard({
  options,
  ctaLabel,
  ctaClassName,
}: {
  /** Sorted by session count; the smallest is shown first, as on /book. */
  options: LandingPackageOptionView[];
  ctaLabel: string;
  ctaClassName: string;
}) {
  const [chosenId, setChosenId] = useState(options[0]?.id);
  const chosen = options.find((option) => option.id === chosenId) ?? options[0];

  if (!chosen) return null;

  return (
    <>
      {chosen.title}

      <PackageSessionOptionSelect
        className="mt-3"
        options={options.map((option) => ({
          id: option.id,
          booking_count: option.bookingCount,
        }))}
        value={chosen.id}
        onValueChange={setChosenId}
      />

      {chosen.content}

      <Link
        href={`/packages?tab=purchase&package=${chosen.id}`}
        className={ctaClassName}
        data-test="landing-package-session-options-cta"
      >
        {ctaLabel}
      </Link>
    </>
  );
}
